import { useCallback, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { roleAtLeast, rolesAtLeast } from '@lms/shared-types'
import type { Role } from '@lms/shared-types'
import {
  ALL_NAV_ITEMS,
  NAV_ITEMS,
  NAV_SECTION_LABEL,
  NAV_SECTION_ORDER,
  type NavCounts,
  type NavItem,
  type NavSection,
} from './nav-config'
import { Icon } from '../primitives/Icon'
import { Tooltip } from '../primitives/Tooltip'
import { buildVersion } from '../../lib/build-version'

/**
 * Slice U6 sidebar — viewport-aware.
 *
 * Variants:
 *   - "expanded" (default, desktop ≥1100px) : 190px wide, full labels,
 *     sticky under the 60px TopBar, height fills the remaining viewport.
 *   - "collapsed" (tablet 720-1099)         : 56px wide icon-rail with
 *     right-side Tooltip on each nav icon.
 *   - "drawer"   (mobile <720px)            : full labels again, but no
 *     sticky positioning — MobileDrawer owns the chrome.
 *
 * Brand title and sign-out live in the TopBar / MobileDrawer. The sidebar's
 * job is just nav.
 */

// Single accent for every nav icon — the "Apply leave" sky blue, which sits in
// the navy→azure masthead family. (Kept as a function so callers stay
// unchanged; the route argument is intentionally ignored now that one hue is
// used everywhere.)
const NAV_ICON_COLOR = '#0EA5E9'

export function navIconColor(_routeKey: string): string {
  return NAV_ICON_COLOR
}

// Selected-row highlight — the royal navy mid-stop of the TopBar masthead
// gradient (`linear-gradient(100deg, #0e2a5e, #1b48a6, #3f86ea)`), so the
// active nav item echoes the brand chrome. Deep enough that the row's label +
// icon flip to white for contrast.
const NAV_ACTIVE_BG = '#1b48a6'

const SIDEBAR_WIDTH_EXPANDED = 190
const SIDEBAR_WIDTH_COLLAPSED = 56
const SIDEBAR_BRAND_HEIGHT = 56
const SIDEBAR_WIDTH_MIN = 160
const SIDEBAR_WIDTH_MAX = 360
const SIDEBAR_RESIZE_HANDLE_WIDTH = 5

export type SidebarVariant = 'expanded' | 'collapsed' | 'drawer'

export interface SidebarProps {
  /** The role used to render the nav set + stats box. May be the real
   *  role, or the impersonated employee's role when "Viewing As" is active. */
  currentRole: Role
  /**
   * Full role set for the authenticated user. When provided, nav item
   * visibility is evaluated against the set so multi-role users (e.g.
   * hr+manager) see items for all their roles. Falls back to [currentRole]
   * when absent or empty.
   */
  currentRoles?: Role[]
  /** Visual mode. Defaults to expanded; AppShell flips this based on
   *  viewport + collapse state. */
  variant?: SidebarVariant
  /** When provided, renders a hamburger toggle at the top of the sidebar
   *  that flips between expanded and collapsed (icon-rail) variants. Not
   *  rendered in `drawer` mode — the MobileDrawer owns its own chrome. */
  onToggle?: () => void
  /**
   * Live pending-action counts keyed by NavCountKey. Populated by
   * `useNavCounts` in AppShell and passed down so the Sidebar itself stays
   * a pure presentational component (no data-fetching).
   *
   * Missing keys render no badge; zero-valued keys also render no badge.
   */
  counts?: NavCounts
  /**
   * Custom expanded width in pixels. Ignored for `collapsed` (fixed
   * icon-rail) and `drawer` (drawer parent owns width).
   */
  width?: number
  /**
   * Called as the user drags the right-edge resize handle. When omitted,
   * the resize handle is not rendered.
   */
  onResize?: (nextWidth: number) => void
  /**
   * Sticky top offset in px — the height of the full-width TopBar the sidebar
   * now sits beneath. The sidebar sticks at `top: topOffset` and fills the
   * remaining viewport (`100vh - topOffset`). Defaults to 0.
   */
  topOffset?: number
}

/**
 * The single source of truth for nav-item visibility. Both the desktop
 * Sidebar and the mobile bottom-nav filter through this so they can never
 * drift (the mobile bar additionally `.slice`s to its first N items).
 *
 * - `role` is the primary/effective single role (used only for the
 *   `hiddenForRoleAtLeast` ceiling).
 * - `roles` is the full role set (used for `minimumRole` and `excludeForRoles`).
 */
export function visibleItems(role: Role, roles: Role[]): NavItem[] {
  // Superadmin sees the entire sidebar — every section, ignoring feature flags
  // and all role/exclusion rules. No exceptions (route access is already open
  // to superadmin via RequireRole's apex bypass).
  if (roles.includes('superadmin')) return [...ALL_NAV_ITEMS]
  return NAV_ITEMS.filter((item) => {
    // Hierarchy check — any role in the set may satisfy the minimum.
    if (!rolesAtLeast(roles, item.minimumRole)) return false
    // Upper-bound check — hide once the PRIMARY role exceeds the ceiling.
    if (item.hiddenForRoleAtLeast && roleAtLeast(role, item.hiddenForRoleAtLeast)) {
      return false
    }
    // Exclusion check — hide only when EVERY role in the set is excluded.
    // A user with {hr, manager} should still see manager items even though
    // 'hr' appears in excludeForRoles, because their 'manager' role is not.
    if (item.excludeForRoles && roles.every((r) => item.excludeForRoles!.includes(r))) {
      return false
    }
    // HR sidebar sections are for HR admins AND the Executive Director — but
    // NOT line Directors. The hierarchy ranks director (4) between hr (3) and
    // exec_director (5), so a plain `minimumRole: 'hr'` would leak these to
    // directors. Gate on literal membership of hr OR exec_director (superadmin
    // already bypasses above). Mirrors the backend require_hr_or_exec_director.
    if (
      item.section.startsWith('hr-') &&
      !(roles.includes('hr') || roles.includes('exec_director'))
    )
      return false
    return true
  })
}

interface IndexedItem {
  item: NavItem
  isChild: boolean
}

function indexSection(items: ReadonlyArray<NavItem>): IndexedItem[] {
  return items.map((item) => ({ item, isChild: false }))
}

export function Sidebar({
  currentRole,
  currentRoles,
  variant = 'expanded',
  onToggle,
  counts = {},
  width,
  onResize,
  topOffset = 0,
}: SidebarProps): JSX.Element {
  const roles = currentRoles && currentRoles.length > 0 ? currentRoles : [currentRole]
  const location = useLocation()

  const items = visibleItems(currentRole, roles)
  const visibleSections = NAV_SECTION_ORDER.filter((s) =>
    items.some((it) => it.section === s),
  )

  const sectionLabel = (section: NavSection): string => NAV_SECTION_LABEL[section]
  const collapsed = variant === 'collapsed'
  const inDrawer = variant === 'drawer'
  const expandedWidth =
    width != null
      ? Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(width)))
      : SIDEBAR_WIDTH_EXPANDED
  const renderedWidth: number | string = collapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : inDrawer
      ? '100%'
      : expandedWidth

  const [isResizing, setIsResizing] = useState(false)
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null)

  const handleResizeMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      if (!onResize) return
      e.preventDefault()
      e.stopPropagation()
      dragRef.current = { startX: e.clientX, startWidth: expandedWidth }
      setIsResizing(true)

      const handleMove = (ev: globalThis.MouseEvent): void => {
        const state = dragRef.current
        if (!state) return
        onResize(state.startWidth + (ev.clientX - state.startX))
      }
      const handleUp = (): void => {
        dragRef.current = null
        setIsResizing(false)
        window.removeEventListener('mousemove', handleMove)
        window.removeEventListener('mouseup', handleUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [expandedWidth, onResize],
  )

  const handleAsideClick = useCallback(
    (e: ReactMouseEvent<HTMLElement>): void => {
      if (!onToggle || inDrawer) return
      if (e.button !== 0) return
      const target = e.target as HTMLElement | null
      if (target?.closest('a, button, [data-resize-handle]')) return
      onToggle()
    },
    [onToggle, inDrawer],
  )

  const showResizeHandle = !collapsed && !inDrawer && onResize != null

  // Sticky positioning only makes sense on the standalone desktop/tablet
  // shell. Inside the mobile drawer the parent already manages position.
  const stickyStyles = inDrawer
    ? {}
    : {
        position: 'sticky' as const,
        top: topOffset,
        height: `calc(100vh - ${topOffset}px)`,
      }

  return (
    <aside
      data-testid="sidebar"
      data-variant={variant}
      onClick={onToggle && !inDrawer ? handleAsideClick : undefined}
      style={{
        width: renderedWidth,
        flexShrink: 0,
        background: 'var(--surface)',
        borderRight: inDrawer ? 'none' : '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        alignSelf: inDrawer ? 'auto' : 'stretch',
        cursor: onToggle && !inDrawer ? 'pointer' : 'default',
        userSelect: onToggle && !inDrawer ? 'none' : 'auto',
        WebkitTapHighlightColor: 'transparent',
        // Smooth expand/collapse — width tween on the rail, the brand
        // labels fade in their own block. Drawer variant skips the tween
        // (parent slide owns the motion). Disabled mid-drag so the rail
        // tracks the cursor 1:1.
        transition:
          inDrawer || isResizing
            ? 'none'
            : 'width var(--motion-duration-slow) var(--motion-ease-out)',
        overflow: 'hidden',
        ...stickyStyles,
      }}
    >
      {/* Brand (logo + name) lives in the full-width TopBar now; the sidebar
          keeps only its own collapse toggle at the top. */}
      {!inDrawer && onToggle ? (
        <SidebarToggle collapsed={collapsed} onToggle={onToggle} />
      ) : null}
      <nav
        style={{
          flex: 1,
          padding: collapsed ? '4px 4px 4px' : '4px 8px 4px',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {visibleSections.map((section, sectionIdx) => {
          const sectionItems = items.filter((it) => it.section === section)
          const indexed = indexSection(sectionItems)
          return (
            <div
              key={section}
              data-section={section}
              style={{ marginTop: sectionIdx === 0 ? 0 : 14 }}
            >
              {!collapsed ? (
                <SectionHeader section={section} label={sectionLabel(section)} />
              ) : (
                <SectionDivider />
              )}
              {indexed.map(({ item, isChild }) => {
                  const count = item.countKey != null ? counts[item.countKey] : undefined
                  return (
                    <NavLinkRow
                      key={item.to}
                      item={item}
                      isChild={isChild}
                      pathname={location.pathname}
                      collapsed={collapsed}
                      {...(count != null ? { count } : {})}
                    />
                  )
                })}
            </div>
          )
        })}
      </nav>
      <SidebarVersion collapsed={collapsed} />
      {showResizeHandle ? (
        <div
          data-resize-handle
          data-testid="sidebar-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          onMouseDown={handleResizeMouseDown}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: SIDEBAR_RESIZE_HANDLE_WIDTH,
            height: '100%',
            cursor: 'col-resize',
            background: isResizing ? 'var(--steel-blue)' : 'transparent',
            opacity: isResizing ? 0.4 : 1,
            transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
            zIndex: 1,
          }}
          onMouseEnter={(e) => {
            if (!isResizing) {
              e.currentTarget.style.background = 'var(--border)'
            }
          }}
          onMouseLeave={(e) => {
            if (!isResizing) {
              e.currentTarget.style.background = 'transparent'
            }
          }}
        />
      ) : null}
    </aside>
  )
}

interface SidebarToggleProps {
  collapsed: boolean
  onToggle: () => void
}

/**
 * Collapse/expand control at the top of the sidebar. The brand (logo + name)
 * now lives in the full-width TopBar, so the sidebar keeps only this toggle —
 * right-aligned when expanded, centered in the icon-rail when collapsed.
 */
export function SidebarToggle({ collapsed, onToggle }: Readonly<SidebarToggleProps>): JSX.Element {
  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar'
  return (
    <div
      data-testid="sidebar-toggle-row"
      style={{
        height: SIDEBAR_BRAND_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-end',
        padding: collapsed ? '0 8px' : '0 12px',
        minWidth: 0,
      }}
    >
      <Tooltip content={toggleLabel} placement="right">
        <button
          type="button"
          onClick={onToggle}
          aria-label={toggleLabel}
          aria-pressed={collapsed}
          data-testid="sidebar-toggle"
          style={{
            width: collapsed ? 40 : 28,
            height: collapsed ? 40 : 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            borderRadius: 8,
            color: 'var(--text-3)',
            cursor: 'pointer',
            flexShrink: 0,
            padding: 0,
            transition:
              'width var(--motion-duration-slow) var(--motion-ease-out), height var(--motion-duration-slow) var(--motion-ease-out), background var(--motion-duration-fast) var(--motion-ease-out), color var(--motion-duration-fast) var(--motion-ease-out)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--surface-2)'
            e.currentTarget.style.color = 'var(--text)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--text-3)'
          }}
        >
          <Icon name={collapsed ? 'right_panel_close' : 'right_panel_open'} size={20} aria-hidden />
        </button>
      </Tooltip>
    </div>
  )
}

/**
 * Build-version footer pinned to the bottom of the sidebar so anyone can see
 * which bundle the running app was built from (the deployed short git SHA, or
 * `dev` locally). Collapsed icon-rail mode shows just the value, centered,
 * with the full label on hover.
 */
function SidebarVersion({ collapsed }: Readonly<{ collapsed: boolean }>): JSX.Element {
  const version = buildVersion()
  const baseStyle = {
    flexShrink: 0,
    padding: collapsed ? '6px 4px' : '8px 16px',
    borderTop: '1px solid var(--border)',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '0.04em',
    color: 'var(--text-3)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center' as const,
  }
  if (collapsed) {
    return (
      <Tooltip content={`Build ${version}`} placement="right">
        <div data-testid="sidebar-version" style={baseStyle}>
          {version}
        </div>
      </Tooltip>
    )
  }
  return (
    <div data-testid="sidebar-version" title={`Build ${version}`} style={baseStyle}>
      {version}
    </div>
  )
}

interface SectionHeaderProps {
  section: NavSection
  /** Resolved label — Sidebar may override the static map for runtime
   *  sections (e.g. "Acting for Hafiz" when an EA is in approve mode). */
  label: string
}

function SectionHeader({ section, label }: SectionHeaderProps): JSX.Element {
  return (
    <div
      data-testid="nav-section-header"
      data-section={section}
      style={{
        padding: '8px 12px 4px',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--text-3)',
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  )
}

function SectionDivider(): JSX.Element {
  // In collapsed/icon-rail mode we drop section headers but keep a thin
  // divider so sections remain visually distinct.
  return (
    <div
      aria-hidden
      style={{
        height: 1,
        margin: '6px 8px',
        background: 'var(--french-gray-2)',
      }}
    />
  )
}

interface NavLinkRowProps {
  item: NavItem
  isChild: boolean
  pathname: string
  collapsed: boolean
  /** Pending-action count. Badge is hidden when undefined or 0. */
  count?: number
}

function NavCountBadge({ count }: Readonly<{ count: number }>): JSX.Element {
  const label = count > 99 ? '99+' : String(count)
  return (
    <span
      aria-hidden
      data-testid="nav-count-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--surface-2)',
        color: 'var(--text)',
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
        padding: '1px 6px',
        borderRadius: 4,
        flexShrink: 0,
        minWidth: 20,
      }}
    >
      {label}
    </span>
  )
}

function NavCountDot({ count }: Readonly<{ count: number }>): JSX.Element {
  return (
    <span
      data-testid="nav-count-dot"
      aria-label={`${count} pending`}
      style={{
        position: 'absolute',
        top: 6,
        right: 6,
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: 'var(--steel-blue)',
        border: '1.5px solid var(--surface)',
        flexShrink: 0,
        display: 'block',
      }}
    />
  )
}

function NavLinkRow({ item, isChild, pathname, collapsed, count }: NavLinkRowProps): JSX.Element {
  const active = pathname === item.to
  const hasCount = count != null && count > 0

  if (collapsed) {
    const ariaLabel = hasCount ? `${item.label}, ${count} pending` : item.label
    return (
      <Tooltip content={item.label} placement="right">
        <Link
          to={item.to}
          aria-label={ariaLabel}
          data-active={active ? 'true' : 'false'}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 44,
            minWidth: 44,
            margin: '2px 4px',
            borderRadius: 8,
            color: active ? 'var(--surface)' : 'var(--text-2)',
            background: active ? NAV_ACTIVE_BG : 'transparent',
          }}
        >
          <Icon
            name={item.icon}
            size={22}
            fill={active}
            color={active ? 'var(--surface)' : navIconColor(item.to)}
            aria-hidden
          />
          {hasCount ? <NavCountDot count={count} /> : null}
        </Link>
      </Tooltip>
    )
  }

  const ariaLabel = hasCount ? `${item.label}, ${count} pending` : undefined
  const iconColor = navIconColor(item.to)

  return (
    <Link
      to={item.to}
      aria-label={ariaLabel}
      data-active={active ? 'true' : 'false'}
      data-child={isChild ? 'true' : 'false'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 36,
        padding: '8px 12px',
        paddingLeft: isChild ? 24 : 12,
        marginLeft: isChild ? 8 : 0,
        borderLeft: isChild ? '2px solid var(--border)' : 'none',
        borderRadius: isChild ? '0 8px 8px 0' : 8,
        fontSize: 13,
        // Active row reverses onto the azure highlight — white label + icon;
        // inactive keeps a neutral grey label so the coloured icon leads.
        color: active ? 'var(--surface)' : 'var(--text-2)',
        // Selected row gets the azure highlight from the masthead gradient so it
        // reads as the current page at a glance, not just a bolder label.
        background: active ? NAV_ACTIVE_BG : 'transparent',
        fontWeight: active ? 600 : 400,
        marginBottom: 2,
        lineHeight: 1.4,
      }}
    >
      <Icon
        name={item.icon}
        size={18}
        fill={active}
        color={active ? 'var(--surface)' : iconColor}
        aria-hidden
      />
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.label}
      </span>
      {hasCount ? <NavCountBadge count={count} /> : null}
    </Link>
  )
}
